'use client';

import styles from './SkeletonLoader.module.css';

interface SkeletonLoaderProps {
  variant: 'card' | 'chart' | 'table' | 'text' | 'kpi-grid' | 'tab-bar' | 'chat' | 'form' | 'calendar';
  count?: number;
  height?: string;
  className?: string;
}

export default function SkeletonLoader({ variant, count = 1, height, className }: SkeletonLoaderProps) {
  const customStyle = height ? { height } : undefined;

  switch (variant) {
    case 'kpi-grid':
      return (
        <div className={`${styles.kpiGrid} ${className || ''}`}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className={`${styles.skeleton} ${styles.kpiCard}`} />
          ))}
        </div>
      );

    case 'chart':
      return (
        <div className={`${styles.container} ${className || ''}`}>
          {Array.from({ length: count }).map((_, i) => (
            <div key={i} className={`${styles.skeleton} ${styles.chart}`} style={customStyle} />
          ))}
        </div>
      );

    case 'table':
      return (
        <div className={`${styles.table} ${className || ''}`}>
          <div className={`${styles.skeleton} ${styles.tableHeader}`} />
          {Array.from({ length: count }).map((_, i) => (
            <div key={i} className={`${styles.skeleton} ${styles.tableRow}`} />
          ))}
        </div>
      );

    case 'text':
      return (
        <div className={className || ''}>
          {Array.from({ length: count }).map((_, i) => (
            <div key={i} className={`${styles.skeleton} ${styles.textLine}`} />
          ))}
        </div>
      );

    case 'card':
      return (
        <div className={className || ''}>
          {Array.from({ length: count }).map((_, i) => (
            <div key={i} className={`${styles.skeleton} ${styles.card}`} style={customStyle} />
          ))}
        </div>
      );

    case 'tab-bar':
      return (
        <div className={`${styles.tabBar} ${className || ''}`}>
          {Array.from({ length: count }).map((_, i) => (
            <div key={i} className={`${styles.skeleton} ${styles.tab}`} />
          ))}
        </div>
      );

    case 'chat':
      return (
        <div className={`${styles.chatLayout} ${className || ''}`}>
          <div className={styles.chatSidebar}>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className={`${styles.skeleton} ${styles.chatItem}`} />
            ))}
          </div>
          <div className={styles.chatMain}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className={`${styles.skeleton} ${styles.chatBubble}`} />
            ))}
          </div>
        </div>
      );

    case 'form':
      return (
        <div className={`${styles.formGroup} ${className || ''}`}>
          {Array.from({ length: count }).map((_, i) => (
            <div key={i}>
              <div className={`${styles.skeleton} ${styles.formLabel}`} />
              <div className={`${styles.skeleton} ${styles.formInput}`} style={{ marginTop: 8 }} />
            </div>
          ))}
        </div>
      );

    case 'calendar':
      return (
        <div className={`${styles.calendarGrid} ${className || ''}`}>
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className={`${styles.skeleton} ${styles.calendarHeader}`} />
          ))}
          {Array.from({ length: 35 }).map((_, i) => (
            <div key={i} className={`${styles.skeleton} ${styles.calendarCell}`} />
          ))}
        </div>
      );

    default:
      return <div className={`${styles.skeleton} ${styles.card}`} style={customStyle} />;
  }
}
